// src/hooks/useCalendarContactCheck.js
import { useQuery } from 'react-query';
import { fetchUtils } from 'react-admin';
import { createTenantQueryKey, extractTenantFromHostname } from '../utils/tenant';

// Helper function for HTTP requests with tenant awareness
const httpClient = (url, options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  const token = localStorage.getItem('token');
  options.headers.set('Authorization', `Bearer ${token}`);

  // Get API URL with proper tenant
  const tenant = extractTenantFromHostname();

  // Determine base API URL (read from runtime config)
  const nodeEnv = window.APP_CONFIG?.NODE_ENV || 'production';

  let apiUrl;
  if (nodeEnv === 'development') {
    apiUrl = `http://${tenant}.api.wygc.eu:8080/api`;
  } else {
    apiUrl = `https://${tenant}.api.wygc.eu/api`;
  }

  // Ensure the URL is properly formed
  const fullUrl = url.startsWith('http') ? url : `${apiUrl}/${url.replace(/^\//, '')}`;

  console.log(`Calendar contact check to: ${fullUrl}`);

  return fetchUtils.fetchJson(fullUrl, options);
};

/**
 * Custom hook to check if all calendar events can match with team contact keywords
 * @param {string} teamId - The team ID
 * @param {Object} options - Additional React Query options
 * @returns {Object} Query result with calendar contact matching analysis
 */
export const useCalendarContactCheck = (teamId, options = {}) => {
  const tenant = extractTenantFromHostname();

  return useQuery(
    createTenantQueryKey(['calendarContactCheck', teamId, tenant]),
    async () => {
      if (!teamId) {
        throw new Error('Team ID is required');
      }

      const checkUrl = `teams/${teamId}/calendars/check_contacts`;
      try {
        const { json } = await httpClient(checkUrl);
        return json;
      } catch (error) {
        console.error('Error checking calendar contacts:', error);
        throw error;
      }
    },
    {
      enabled: !!teamId,
      staleTime: 30 * 60 * 1000,      // 30 minutes
      cacheTime: 60 * 60 * 1000,      // 60 minutes
      refetchOnWindowFocus: false,    // Don't refetch when window regains focus
      retry: false,                   // Don't retry failed requests automatically
      ...options                      // Allow overriding default options
    }
  );
};